import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BookOpen, CircleCheck } from 'lucide-react';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { LabeledPillList, type PillField } from '@/components/common/LabeledPill';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { assignmentSubjectsQueryOptions } from '../api/question.api';
import { PaperManagerDialog } from '../components/PaperManagerDialog';
import { getInstitutePaperColumns } from '../components/QuestionColumns';
import { INSTITUTE_PAPERS_TITLE, PROJECT_MANAGEMENT_PATH } from '../constants/question.constants';
import { useInstituteHolder } from '../hooks/useInstituteHolder';
import type {
  InstitutePapersSearch,
  MonitorDetailRow,
  UploadSubject,
} from '../types/question.types';
import { getHolderPills } from '../utils/holderPills';
import { toCountPillField } from '../utils/monitorLevels';

function getPaperCountPills(holder: MonitorDetailRow | undefined, isLoading: boolean): PillField[] {
  return [
    toCountPillField({
      key: 'subjects',
      label: 'Subjects',
      count: holder?.assignedCount,
      isLoading,
      icon: BookOpen,
    }),
    toCountPillField({
      key: 'collected',
      label: 'Collected',
      count: holder?.collectedCount,
      isLoading,
      icon: CircleCheck,
    }),
  ];
}

interface InstitutePapersPageProps {
  projectInstituteId: number;
  search: InstitutePapersSearch;
}

export function InstitutePapersPage({ projectInstituteId, search }: InstitutePapersPageProps) {
  const navigate = useNavigate();
  const [subject, setSubject] = useState<UploadSubject | null>(null);

  useRememberViewedRow('questionInstitutePapers', projectInstituteId);

  const subjectsQuery = useQuery(assignmentSubjectsQueryOptions(projectInstituteId));
  const { holder, isLoading: isHolderLoading } = useInstituteHolder(projectInstituteId, search);

  const rows = useMemo(
    () =>
      (subjectsQuery.data ?? []).filter(
        (uploadSubject) => !search.isCollectedOnly || uploadSubject.uploadCount > 0,
      ),
    [subjectsQuery.data, search.isCollectedOnly],
  );
  const columns = useMemo(() => getInstitutePaperColumns(setSubject), []);

  function handleNoHistory() {
    if (search.from === PROJECT_MANAGEMENT_PATH) {
      void navigate({
        to: PROJECT_MANAGEMENT_PATH,
        search: search.isCollectedOnly
          ? { projectId: search.projectId, tab: 'assignments', userId: search.userId }
          : { projectId: search.projectId, tab: 'institutes' },
      });
      return;
    }
    void navigate({ to: search.from, search: { projectId: search.projectId, tab: 'institutes' } });
  }

  return (
    <>
      <DetailTableLayout
        title={
          search.isCollectedOnly ? INSTITUTE_PAPERS_TITLE.collectedOnly : INSTITUTE_PAPERS_TITLE.all
        }
        onNoHistory={handleNoHistory}
        summary={
          <LabeledPillList
            fields={[
              ...getHolderPills(holder, isHolderLoading),
              ...getPaperCountPills(holder, isHolderLoading),
            ]}
          />
        }
        table={{
          rows,
          columns,
          isLoading: subjectsQuery.isLoading,
          error: subjectsQuery.error,
          errorTitle: 'Could not load the subjects',
          onRetry: () => void subjectsQuery.refetch(),
          stickyColumnId: 'subjectName',
          emptyMessage: search.isCollectedOnly
            ? 'No paper is in for this institute yet'
            : 'No subject is set for this institute',
        }}
      />

      <PaperManagerDialog
        subject={subject}
        instituteName={holder?.instituteName ?? null}
        canWrite={false}
        onOpenChange={(open) => {
          if (!open) setSubject(null);
        }}
      />
    </>
  );
}
